import posthog, { PostHogConfig } from "posthog-js";
import { PostHog, PostHogProvider, usePostHog } from "posthog-js/react";

type BaseHflPostHogConfig = {
  vertikalName: string;
  environment: string;
  debugInConsole: boolean;
  postHogApiKey?: string | null;
  nonce: string;
  onInit?: (posthog: PostHog) => void;
  moreConfig?: Partial<PostHogConfig>;
};

type HflPostHogInternalFields = {
  brukerId: string | null;
  brukerIsSystemforvalter: boolean;
}

type HflPostHogExternalFields = {
  orgnummer: string | null;
  eksternRolle?: string | undefined;
}

export type InternalHflPostHogConfig = BaseHflPostHogConfig & HflPostHogInternalFields

export type ExternalHflPostHogConfig = BaseHflPostHogConfig & HflPostHogExternalFields

type HflPostHogConfig = BaseHflPostHogConfig & HflPostHogInternalFields & HflPostHogExternalFields & {
  isExternal: boolean
}

// Denne brukes der brukeren logger inn på vegne av en organisasjon (eksternt)
export const setPosthogOrgnummer = (hflPosthog: PostHog, orgnummer: string, eksternRolle: string | undefined) => {
  hflPosthog.group("orgnummer", orgnummer, {
    orgnummer,
  });
  if (eksternRolle) {
    hflPosthog.group("rolle", eksternRolle, {
      rolle: eksternRolle,
    });
  }
}

const initializeHflPostHogClient = (config: HflPostHogConfig) => {
  const {
    vertikalName,
    environment,
    orgnummer,
    eksternRolle,
    brukerId,
    brukerIsSystemforvalter,
    postHogApiKey,
    debugInConsole = false,
    isExternal,
    nonce,
    onInit,
    moreConfig = {},
  } = config;

  if (postHogApiKey) {
    posthog.init(postHogApiKey, {
      api_host: "https://i.udir.tech",
      ui_host: "https://eu.posthog.com",
      capture_exceptions: true,
      cookieless_mode: isExternal ? "on_reject" : undefined,
      defaults: "2025-05-24",
      autocapture: false,
      debug: debugInConsole,
      ...moreConfig,
      before_send: (event) => {
        if (
          event &&
          !event.event.startsWith("$") &&
          !event.event.startsWith("survey ")
        ) {
          // eslint-disable-next-line no-param-reassign
          event.event = `${vertikalName}:${event.event}`;
        }
        if (moreConfig.before_send) {
          if (typeof moreConfig.before_send === "function") {
            return moreConfig.before_send(event);
          }
          return moreConfig.before_send.reduce((ev, fn) => fn(ev), event);
        }
        return event;
      },
      prepare_external_dependency_script: (script) => {
        // eslint-disable-next-line no-param-reassign
        script.nonce = nonce;
        if (moreConfig.prepare_external_dependency_script) {
          return moreConfig.prepare_external_dependency_script(script);
        }
        return script;
      },
    });
    posthog.group("vertikal", vertikalName, {
      app: vertikalName,
      external: isExternal,
    });
    posthog.group("environment", environment, {
      name: environment,
    });
    if (orgnummer) {
      setPosthogOrgnummer(posthog, orgnummer, eksternRolle);
    }
    if (brukerId) {
      posthog.identify(brukerId, {
        brukerId,
        brukerIsSystemforvalter,
      });
    }
    if (onInit) onInit(posthog);
  }

  return posthog;
};

export const initializeInternalHflPostHogClient = (config: InternalHflPostHogConfig) => {
  return initializeHflPostHogClient({
    ...config,
    isExternal: false,
    orgnummer: null,
    eksternRolle: undefined,
  });
}

export const initializeExternalHflPostHogClient = (config: ExternalHflPostHogConfig) => {
  return initializeHflPostHogClient({
    ...config,
    isExternal: true,
    brukerId: null,
    brukerIsSystemforvalter: false,
  });
}

export const HflPostHogProvider = PostHogProvider;

export const useHflPostHog = usePostHog;
